import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Message } from "@cloudflare/realtimekit";
import {
  ChevronLeft,
  ChevronRight,
  Monitor,
  MonitorOff,
  Pin,
  Send,
  Radio,
  Square,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JoinStageIcon } from "./icons/JoinStageIcon";
import { getNameInitials } from "./participantName";
import { getLivestreamChatText, useLivestreamChatUnreadCount } from "./useLivestreamChat";
import type { StageStatus } from "@cloudflare/realtimekit";
import type { LivestreamStageRequest } from "./useLivestreamStageHost";
import type { LivestreamPinnedComment } from "./livestreamPinnedComment";
import type { MeetingShopTipping } from "./meetingProductTypes";

const CHAT_SCROLL_BOTTOM_THRESHOLD_PX = 32;
const RECORDING_INITIALIZATION_DELAY_MS = 5000;
const LEAD_OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;

function isChatScrolledToBottom({
  element,
  thresholdPx = CHAT_SCROLL_BOTTOM_THRESHOLD_PX,
}: {
  element: HTMLElement;
  thresholdPx?: number;
}) {
  return (
    element.scrollHeight - element.scrollTop - element.clientHeight <= thresholdPx
  );
}

function formatChatMessageTime({ time }: { time: Date | number | undefined }) {
  if (time == null) return null;
  const date = time instanceof Date ? time : new Date(time);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function getMessageContactId({
  messageUserId,
  participantContactIdsByUserId,
}: {
  messageUserId: string;
  participantContactIdsByUserId: Map<string, string>;
}) {
  const contactId = participantContactIdsByUserId.get(messageUserId)?.trim();
  if (!contactId || !LEAD_OBJECT_ID_PATTERN.test(contactId)) return null;
  return contactId;
}

function getParticipantContactHref({
  baseUrl,
  contactId,
}: {
  baseUrl?: string;
  contactId?: string | null;
}) {
  const normalizedContactId = contactId?.trim();
  if (!baseUrl || !normalizedContactId || !LEAD_OBJECT_ID_PATTERN.test(normalizedContactId)) {
    return null;
  }
  return `${baseUrl.replace(/\/$/, "")}/contacts/${normalizedContactId}`;
}

function LivestreamHostChatPanel({
  messages,
  onSendMessage,
  pinnedComment,
  onTogglePinComment,
  baseUrl,
  participantContactIdsByUserId,
}: {
  messages: Message[];
  onSendMessage: ({ text }: { text: string }) => Promise<void>;
  pinnedComment: LivestreamPinnedComment | null;
  onTogglePinComment: ({
    messageId,
    displayName,
    text,
  }: {
    messageId: string;
    displayName: string;
    text: string;
  }) => Promise<void>;
  baseUrl?: string;
  participantContactIdsByUserId: Map<string, string>;
}) {
  const chatInputRef = useRef<HTMLInputElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const isPinnedToBottomRef = useRef(true);
  const refocusChatAfterSendRef = useRef(false);
  const [chatDraft, setChatDraft] = useState("");
  const [sendingChat, setSendingChat] = useState(false);

  const textMessages = messages
    .map((message) => ({ message, text: getLivestreamChatText(message) }))
    .filter((entry): entry is { message: Message; text: string } => entry.text != null);

  useLayoutEffect(() => {
    if (!isPinnedToBottomRef.current) return;
    const scrollEl = messagesScrollRef.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = scrollEl.scrollHeight;
  }, [messages, textMessages.length]);

  useEffect(() => {
    if (!refocusChatAfterSendRef.current || sendingChat) return;
    refocusChatAfterSendRef.current = false;
    chatInputRef.current?.focus();
  }, [sendingChat]);

  const handleSendChat = async () => {
    if (!chatDraft.trim() || sendingChat) return;
    setSendingChat(true);
    try {
      await onSendMessage({ text: chatDraft });
      setChatDraft("");
      refocusChatAfterSendRef.current = true;
    } finally {
      setSendingChat(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={messagesScrollRef}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3"
        onScroll={() => {
          const scrollEl = messagesScrollRef.current;
          if (!scrollEl) return;
          isPinnedToBottomRef.current = isChatScrolledToBottom({ element: scrollEl });
        }}
      >
        {textMessages.length === 0 ? (
          <p className="text-sm text-white/60">No messages yet.</p>
        ) : (
          textMessages.map(({ message, text }) => {
            const isPinned = pinnedComment?.messageId === message.id;
            const messageTime = formatChatMessageTime({ time: message.time });
            const contactHref = getParticipantContactHref({
              baseUrl,
              contactId: getMessageContactId({
                messageUserId: message.userId,
                participantContactIdsByUserId,
              }),
            });
            return (
              <div
                key={message.id}
                className={`group flex gap-2.5 text-sm ${
                  isPinned ? "rounded-lg border border-amber-400/30 bg-amber-400/10 px-2 py-1.5" : ""
                }`}
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-white"
                  aria-label={message.displayName}
                >
                  {getNameInitials({ name: message.displayName })}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      {contactHref ? (
                        <a
                          href={contactHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-white underline-offset-2 hover:underline"
                        >
                          {message.displayName}
                        </a>
                      ) : (
                        <span className="font-medium text-white">{message.displayName}</span>
                      )}
                      {messageTime ? (
                        <span className="text-xs text-white/40">{messageTime}</span>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className={`h-7 w-7 shrink-0 text-white/50 hover:bg-white/10 hover:text-white ${
                        isPinned ? "text-amber-200" : "opacity-0 group-hover:opacity-100"
                      }`}
                      aria-label={isPinned ? "Unpin comment" : "Pin comment"}
                      onClick={() =>
                        void onTogglePinComment({
                          messageId: message.id,
                          displayName: message.displayName,
                          text,
                        })
                      }
                    >
                      <Pin className={`h-3.5 w-3.5 ${isPinned ? "fill-current rotate-45" : ""}`} />
                    </Button>
                  </div>
                  <p className="mt-0.5 break-words text-white/70">{text}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form
        className="flex items-center gap-2 border-t border-white/10 px-3 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSendChat();
        }}
      >
        <Input
          ref={chatInputRef}
          value={chatDraft}
          onChange={(event) => setChatDraft(event.target.value)}
          placeholder="Say something…"
          disabled={sendingChat}
          className="h-9 flex-1 border-white/20 bg-white/5 text-white placeholder:text-white/50"
          aria-label="Live chat message"
        />
        <Button
          type="submit"
          size="icon"
          variant="outline"
          disabled={!chatDraft.trim() || sendingChat}
          className="h-9 w-9 shrink-0 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          aria-label="Send message"
          onMouseDown={(event) => event.preventDefault()}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

function LivestreamHostParticipantsPanel({
  baseUrl,
  participantContactIdsByUserId,
  participants,
  stageRequests,
  onGrantStageAccess,
  onDenyStageAccess,
  onRemoveFromStage,
  onKickFromMeeting,
  onSelectRandomParticipant,
  selectingRandomParticipant,
  canSelectRandomParticipant,
  randomWinnerName,
  actingOnUserId,
  canGrantStageAccess,
  canKickParticipants,
}: {
  baseUrl?: string;
  participantContactIdsByUserId: Map<string, string>;
  participants: {
    id: string;
    userId: string;
    customParticipantId?: string;
    name: string;
    isSelf: boolean;
    isHost: boolean;
    stageStatus: StageStatus | undefined;
    stageLabel: string;
  }[];
  stageRequests: LivestreamStageRequest[];
  onGrantStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onDenyStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onRemoveFromStage: ({ userId }: { userId: string }) => Promise<void>;
  onKickFromMeeting: ({ participantId }: { participantId: string }) => Promise<void>;
  onSelectRandomParticipant: () => Promise<void>;
  selectingRandomParticipant: boolean;
  canSelectRandomParticipant: boolean;
  randomWinnerName: string | null;
  actingOnUserId: string | null;
  canGrantStageAccess: boolean;
  canKickParticipants: boolean;
}) {
  const sortedParticipants =
    randomWinnerName == null
      ? participants
      : [...participants].sort((left, right) => {
          if (left.name === randomWinnerName) return -1;
          if (right.name === randomWinnerName) return 1;
          return 0;
        });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="space-y-3 px-4 py-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-white/50">
            Join requests
          </p>
          {stageRequests.length === 0 ? (
            <p className="mt-2 text-sm text-white/60">No pending requests.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {stageRequests.map((request) => {
                const busy = actingOnUserId === request.userId;
                const contactHref = getParticipantContactHref({
                  baseUrl,
                  contactId: getMessageContactId({
                    messageUserId: request.userId,
                    participantContactIdsByUserId,
                  }),
                });
                return (
                  <li
                    key={request.userId}
                    className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2"
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-white"
                      aria-label={request.displayName}
                    >
                      {getNameInitials({ name: request.displayName })}
                    </div>
                    {contactHref ? (
                      <a
                        href={contactHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 flex-1 truncate text-sm text-white underline-offset-2 hover:underline"
                      >
                        {request.displayName}
                      </a>
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-sm text-white">
                        {request.displayName}
                      </span>
                    )}
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canGrantStageAccess || busy}
                        className="h-7 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
                        onClick={() => void onGrantStageAccess({ userId: request.userId })}
                      >
                        Accept
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        className="h-7 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
                        onClick={() => void onDenyStageAccess({ userId: request.userId })}
                      >
                        Deny
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-white/50">
              Participants
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canSelectRandomParticipant || selectingRandomParticipant}
              className="h-7 shrink-0 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
              onClick={() => void onSelectRandomParticipant()}
            >
              Select Random
            </Button>
          </div>
          {participants.length === 0 ? (
            <p className="mt-2 text-sm text-white/60">No participants yet.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {sortedParticipants.map((participant) => {
                const busy = actingOnUserId === participant.userId;
                const isRandomWinner = randomWinnerName === participant.name;
                const canRemoveFromStage =
                  !participant.isSelf && participant.stageStatus === "ON_STAGE";
                const canKick =
                  canKickParticipants && !participant.isSelf;
                const contactHref = getParticipantContactHref({
                  baseUrl,
                  contactId: participant.isSelf ? null : participant.customParticipantId,
                });
                return (
                  <li
                    key={participant.id}
                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                      isRandomWinner
                        ? "border-amber-400/40 bg-amber-400/15"
                        : "border-white/10 bg-white/5"
                    }`}
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-white"
                      aria-label={participant.name}
                    >
                      {getNameInitials({ name: participant.name })}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">
                        {contactHref ? (
                          <a
                            href={contactHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline-offset-2 hover:underline"
                          >
                            {participant.name}
                          </a>
                        ) : (
                          participant.name
                        )}
                        {participant.isSelf ? (
                          <span className="ml-1 font-normal text-white/50">(you)</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-white/50">
                        {participant.stageLabel}
                        {isRandomWinner ? (
                          <span className="ml-1.5 font-medium text-amber-200">Winner</span>
                        ) : null}
                      </p>
                    </div>
                    {canRemoveFromStage || canKick ? (
                      <div className="flex shrink-0 gap-1">
                        {canRemoveFromStage ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            className="h-7 shrink-0 border-white/20 bg-white/5 px-2 text-xs text-white hover:bg-white/10 hover:text-white"
                            onClick={() => void onRemoveFromStage({ userId: participant.userId })}
                          >
                            Remove
                          </Button>
                        ) : null}
                        {canKick ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            className="h-7 shrink-0 border-red-500/40 bg-red-500/10 px-2 text-xs text-red-200 hover:bg-red-500/20 hover:text-red-100"
                            onClick={() =>
                              void onKickFromMeeting({ participantId: participant.id })
                            }
                          >
                            Kick
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function LivestreamHostSettingsPanel({
  isCoHost,
  onStage,
  joiningStage,
  leavingStage,
  onJoinStage,
  onLeaveStage,
  isLive,
  goingLive,
  endingStream,
  onGoLive,
  onEndStream,
  screenShareEnabled,
  canShareScreen,
  isRecordingActive,
  recordingBusy,
  onToggleScreenShare,
  requestJoinVisible,
  onRequestJoinVisibleChange,
  hasShopProducts,
  shopTipping,
  shopVisible,
  onShopVisibleChange,
}: {
  isCoHost?: boolean;
  onStage?: boolean;
  joiningStage?: boolean;
  leavingStage?: boolean;
  onJoinStage?: () => void;
  onLeaveStage?: () => void;
  isLive: boolean;
  goingLive: boolean;
  endingStream: boolean;
  onGoLive: () => void;
  onEndStream: () => void;
  screenShareEnabled: boolean;
  canShareScreen: boolean;
  isRecordingActive: boolean;
  recordingBusy: boolean;
  onToggleScreenShare: () => void;
  requestJoinVisible: boolean;
  onRequestJoinVisibleChange: ({ visible }: { visible: boolean }) => void;
  hasShopProducts?: boolean;
  shopTipping?: MeetingShopTipping;
  shopVisible: boolean;
  onShopVisibleChange: ({ visible }: { visible: boolean }) => void;
}) {
  const canEnableShop = hasShopProducts === true || shopTipping?.enabled === true;
  const [showRecordingInitializing, setShowRecordingInitializing] = useState(false);
  const [endStreamConfirmOpen, setEndStreamConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isRecordingActive) {
      setShowRecordingInitializing(false);
      return;
    }

    if (recordingBusy) {
      setShowRecordingInitializing(true);
      return;
    }

    const timer = setTimeout(() => {
      setShowRecordingInitializing(false);
    }, RECORDING_INITIALIZATION_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isRecordingActive, recordingBusy]);

  return (
    <div className="space-y-6 px-4 py-3">
      {isCoHost ? (
        <div className="space-y-2">
          {!onStage ? (
            <Button
              type="button"
              disabled={joiningStage}
              className="w-full gap-2 bg-green-600 text-white hover:bg-green-700"
              onClick={() => void onJoinStage?.()}
            >
              <JoinStageIcon className="h-4 w-4" aria-hidden />
              {joiningStage ? "Joining…" : "Join Stage"}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              disabled={leavingStage}
              className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              onClick={() => void onLeaveStage?.()}
            >
              {leavingStage ? "Leaving…" : "Leave stage"}
            </Button>
          )}
        </div>
      ) : !isLive ? (
        <div className="space-y-2">
          <Button
            type="button"
            disabled={goingLive}
            className="w-full gap-2 bg-green-600 text-white hover:bg-green-700"
            onClick={() => void onGoLive()}
          >
            <Radio className="h-4 w-4" aria-hidden />
            {goingLive ? "Going live…" : "Go Live"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Button
            type="button"
            disabled={endingStream || showRecordingInitializing}
            className={`w-full gap-2 disabled:opacity-100 ${
              showRecordingInitializing
                ? "cursor-not-allowed bg-red-950/50 text-white/45 hover:bg-red-950/50"
                : "bg-red-600 text-white hover:bg-red-700"
            }`}
            onClick={() => setEndStreamConfirmOpen(true)}
          >
            {showRecordingInitializing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Square className="h-4 w-4" aria-hidden />
            )}
            {endingStream
              ? "Ending stream…"
              : showRecordingInitializing
                ? "Initializing Recording"
                : "End Stream"}
          </Button>
        </div>
      )}
      {(isLive || (isCoHost && onStage)) ? (
        <div className="space-y-3">
          <p className="text-sm text-white/60">Stream controls</p>
          <div className="flex flex-col gap-2">
            {canShareScreen ? (
              <Button
                type="button"
                variant="outline"
                className="justify-start gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                onClick={onToggleScreenShare}
              >
                {screenShareEnabled ? (
                  <MonitorOff className="h-4 w-4" />
                ) : (
                  <Monitor className="h-4 w-4" />
                )}
                {screenShareEnabled ? "Stop screen share" : "Share screen"}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="space-y-3">
        <p className="text-sm text-white/60">Viewer controls</p>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
          <div className="min-w-0">
            <Label
              htmlFor="livestream-request-join-visible"
              className="text-sm font-medium text-white"
            >
              Request to join
            </Label>
            <p className="mt-0.5 text-xs text-white/50">
              Let viewers request to join the stage.
            </p>
          </div>
          <Switch
            id="livestream-request-join-visible"
            checked={requestJoinVisible}
            onCheckedChange={(visible) => onRequestJoinVisibleChange({ visible })}
          />
        </div>
        {canEnableShop ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
            <div className="min-w-0">
              <Label
                htmlFor="livestream-shop-visible"
                className="text-sm font-medium text-white"
              >
                Shop
              </Label>
              <p className="mt-0.5 text-xs text-white/50">
                {hasShopProducts
                  ? "Let viewers browse and purchase products."
                  : "Let viewers send tips during the stream."}
              </p>
            </div>
            <Switch
              id="livestream-shop-visible"
              checked={shopVisible}
              onCheckedChange={(visible) => onShopVisibleChange({ visible })}
            />
          </div>
        ) : null}
      </div>
      <Dialog open={endStreamConfirmOpen} onOpenChange={setEndStreamConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End livestream?</DialogTitle>
            <DialogDescription>
              This will end the livestream for all viewers. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEndStreamConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={endingStream}
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                setEndStreamConfirmOpen(false);
                void onEndStream();
              }}
            >
              End stream
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LivestreamHostSidebar({
  isCoHost,
  onStage,
  joiningStage,
  leavingStage,
  onJoinStage,
  onLeaveStage,
  baseUrl,
  messages,
  onSendMessage,
  pinnedComment,
  onTogglePinComment,
  participants,
  stageRequests,
  onGrantStageAccess,
  onDenyStageAccess,
  onRemoveFromStage,
  onKickFromMeeting,
  onSelectRandomParticipant,
  selectingRandomParticipant,
  canSelectRandomParticipant,
  randomWinnerName,
  actingOnUserId,
  canGrantStageAccess,
  canKickParticipants,
  isLive,
  goingLive,
  endingStream,
  onGoLive,
  onEndStream,
  screenShareEnabled,
  canShareScreen,
  isRecordingActive,
  recordingBusy,
  onToggleScreenShare,
  requestJoinVisible,
  onRequestJoinVisibleChange,
  hasShopProducts,
  shopTipping,
  shopVisible,
  onShopVisibleChange,
}: {
  isCoHost?: boolean;
  onStage?: boolean;
  joiningStage?: boolean;
  leavingStage?: boolean;
  onJoinStage?: () => void;
  onLeaveStage?: () => void;
  baseUrl?: string;
  messages: Message[];
  onSendMessage: ({ text }: { text: string }) => Promise<void>;
  pinnedComment: LivestreamPinnedComment | null;
  onTogglePinComment: ({
    messageId,
    displayName,
    text,
  }: {
    messageId: string;
    displayName: string;
    text: string;
  }) => Promise<void>;
  participants: {
    id: string;
    userId: string;
    customParticipantId?: string;
    name: string;
    isSelf: boolean;
    isHost: boolean;
    stageStatus: StageStatus | undefined;
    stageLabel: string;
  }[];
  stageRequests: LivestreamStageRequest[];
  onGrantStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onDenyStageAccess: ({ userId }: { userId: string }) => Promise<void>;
  onRemoveFromStage: ({ userId }: { userId: string }) => Promise<void>;
  onKickFromMeeting: ({ participantId }: { participantId: string }) => Promise<void>;
  onSelectRandomParticipant: () => Promise<void>;
  selectingRandomParticipant: boolean;
  canSelectRandomParticipant: boolean;
  randomWinnerName: string | null;
  actingOnUserId: string | null;
  canGrantStageAccess: boolean;
  canKickParticipants: boolean;
  isLive: boolean;
  goingLive: boolean;
  endingStream: boolean;
  onGoLive: () => void;
  onEndStream: () => void;
  screenShareEnabled: boolean;
  canShareScreen: boolean;
  isRecordingActive: boolean;
  recordingBusy: boolean;
  onToggleScreenShare: () => void;
  requestJoinVisible: boolean;
  onRequestJoinVisibleChange: ({ visible }: { visible: boolean }) => void;
  hasShopProducts?: boolean;
  shopTipping?: MeetingShopTipping;
  shopVisible: boolean;
  onShopVisibleChange: ({ visible }: { visible: boolean }) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("chat");
  const unreadChatCount = useLivestreamChatUnreadCount({
    messages,
    isChatVisible: !collapsed && activeTab === "chat",
  });
  const participantContactIdsByUserId = new Map(
    participants
      .filter(
        (participant) =>
          !participant.isSelf &&
          participant.customParticipantId &&
          LEAD_OBJECT_ID_PATTERN.test(participant.customParticipantId),
      )
      .map((participant) => [participant.userId, participant.customParticipantId!]),
  );

  return (
    <aside
      className={`relative flex h-full shrink-0 flex-col border-l border-white/10 bg-zinc-950 text-white transition-[width] duration-200 ease-linear ${
        collapsed ? "w-10" : "w-72 sm:w-80"
      }`}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setCollapsed((value) => !value)}
        className="absolute -left-3 top-3 z-10 h-6 w-6 rounded-full border border-white/10 bg-zinc-950 text-white/70 shadow-sm hover:bg-zinc-900 hover:text-white"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </Button>
      {collapsed ? null : (
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsList className="mx-3 mt-3 grid w-auto grid-cols-3 bg-white/10">
            <TabsTrigger
              value="chat"
              className="text-xs text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white sm:text-sm"
            >
              Chat
              {unreadChatCount > 0 ? (
                <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] leading-none">
                  {unreadChatCount}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger
              value="people"
              className="text-xs text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white sm:text-sm"
            >
              People
              {stageRequests.length > 0 ? (
                <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] leading-none">
                  {stageRequests.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="text-xs text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white sm:text-sm"
            >
              Settings
            </TabsTrigger>
          </TabsList>
          <TabsContent value="chat" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
            <LivestreamHostChatPanel
              messages={messages}
              onSendMessage={onSendMessage}
              pinnedComment={pinnedComment}
              onTogglePinComment={onTogglePinComment}
              baseUrl={baseUrl}
              participantContactIdsByUserId={participantContactIdsByUserId}
            />
          </TabsContent>
          <TabsContent
            value="people"
            className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <LivestreamHostParticipantsPanel
              baseUrl={baseUrl}
              participantContactIdsByUserId={participantContactIdsByUserId}
              participants={participants}
              stageRequests={stageRequests}
              onGrantStageAccess={onGrantStageAccess}
              onDenyStageAccess={onDenyStageAccess}
              onRemoveFromStage={onRemoveFromStage}
              onKickFromMeeting={onKickFromMeeting}
              onSelectRandomParticipant={onSelectRandomParticipant}
              selectingRandomParticipant={selectingRandomParticipant}
              canSelectRandomParticipant={canSelectRandomParticipant}
              randomWinnerName={randomWinnerName}
              actingOnUserId={actingOnUserId}
              canGrantStageAccess={canGrantStageAccess}
              canKickParticipants={canKickParticipants}
            />
          </TabsContent>
          <TabsContent
            value="settings"
            className="mt-0 min-h-0 flex-1 overflow-y-auto data-[state=inactive]:hidden"
          >
            <LivestreamHostSettingsPanel
              isCoHost={isCoHost}
              onStage={onStage}
              joiningStage={joiningStage}
              leavingStage={leavingStage}
              onJoinStage={onJoinStage}
              onLeaveStage={onLeaveStage}
              isLive={isLive}
              goingLive={goingLive}
              endingStream={endingStream}
              onGoLive={onGoLive}
              onEndStream={onEndStream}
              screenShareEnabled={screenShareEnabled}
              canShareScreen={canShareScreen}
              isRecordingActive={isRecordingActive}
              recordingBusy={recordingBusy}
              onToggleScreenShare={onToggleScreenShare}
              requestJoinVisible={requestJoinVisible}
              onRequestJoinVisibleChange={onRequestJoinVisibleChange}
              hasShopProducts={hasShopProducts}
              shopTipping={shopTipping}
              shopVisible={shopVisible}
              onShopVisibleChange={onShopVisibleChange}
            />
          </TabsContent>
        </Tabs>
      )}
    </aside>
  );
}
